// Real Keycloak SSO is the default (deployed builds always authenticate and send a bearer token).
// LOCAL DEV ONLY: set VUE_APP_BYPASS_AUTH=true (e.g. in .env.local, which is gitignored) to bypass
// Keycloak and send NO bearer token so a QAF-pointed backend runs anonymously (see-all). Because
// this is env-driven and defaults to false, a normal build can no longer accidentally ship the
// bypass (which previously surfaced as "localdev" being recorded as the updating user).
const BYPASS_AUTH = process.env.VUE_APP_BYPASS_AUTH === 'true'

const TOKEN = 'user-token'
const ROLE = 'user-roles'
const USERNAME = 'user-name'
const SAD_ROLE_PREFIX = 'mcs_roles.transaction.suspicious_activity_detection.'

function storeAuthDetails (keycloak) {
    localStorage.setItem(TOKEN, keycloak.token)
    localStorage.setItem(ROLE, getSADRoleFromParsedToken(keycloak.tokenParsed))
    if (keycloak.tokenParsed && keycloak.tokenParsed.preferred_username) {
        localStorage.setItem(USERNAME, keycloak.tokenParsed.preferred_username)
    }
}
function removeAuthDetails () {
    localStorage.removeItem(TOKEN)
    localStorage.removeItem(ROLE)
    localStorage.removeItem(USERNAME)
}
function getSADRoleFromParsedToken (parsedToken) {
    if (parsedToken && parsedToken.realm_access && parsedToken.realm_access.roles) {
        const allRoles = parsedToken.realm_access.roles
        const sadRole = allRoles.filter(value => value.startsWith(SAD_ROLE_PREFIX))

        if (sadRole.length > 0) {
            return sadRole[0]
        } else {
            return null
        }
    }
    return null
}

function createKeycloakAuth () {
    const Keycloak = require('keycloak-js')
    const keycloak = Keycloak('/keycloak.json')

    return {
        authenticate (renderVueApp) {
            // checkLoginIframe: false disables keycloak-js's hidden login-status iframe, which
            // otherwise polls Keycloak every 5s (checkLoginIframeInterval) to detect SSO session
            // changes. That poll relies on reading the Keycloak session cookie in a third-party
            // iframe; browsers that block third-party cookies (Chrome's default / stricter
            // profiles) make every poll look like a session change, causing the app to
            // re-authenticate and the page to refresh every 5 seconds. We refresh the token on a
            // timer below, so the iframe check is not needed.
            keycloak.init({ onLoad: 'login-required', checkLoginIframe: false }).then((auth) => {
                if (!auth) {
                    window.location.href = '/forbidden'
                }
                storeAuthDetails(keycloak)

                renderVueApp()

                const tokenExpireSeconds = keycloak.tokenParsed.exp + keycloak.timeSkew - (new Date().getTime() / 1000)
                const tokenRefreshIntervalMillis = (tokenExpireSeconds * 1000) / 2

                setTimeout(() => {
                    keycloak.updateToken((tokenExpireSeconds * 60) + 10)
                }, tokenRefreshIntervalMillis)
            }).catch(() => {
                removeAuthDetails()
            })
        },

        logout () {
            if (keycloak != null) {
                keycloak.logout({
                    redirectUri:
                        window.location.origin + '/' ||
                        window.location.protocol + '/' + window.location.host + '/',
                })
                removeAuthDetails()
            }
        },

        getToken () {
            return localStorage.getItem(TOKEN)
        },

        getUsername () {
            return localStorage.getItem(USERNAME)
        },

        canView () {
            const role = localStorage.getItem(ROLE)
            return role ? role.endsWith('view') || role.endsWith('edit') || role.endsWith('add') || role.endsWith('delete') : false
        },
        canEdit () {
            const role = localStorage.getItem(ROLE)
            return role ? role.endsWith('edit') || role.endsWith('add') || role.endsWith('delete') : false
        },
        canAdd () {
            const role = localStorage.getItem(ROLE)
            return role ? role.endsWith('add') || role.endsWith('delete') : false
        },
        canDelete () {
            const role = localStorage.getItem(ROLE)
            return role ? role.endsWith('delete') : false
        },
    }
}

function createBypassAuth () {
    return {
        authenticate (renderVueApp) {
            console.warn('[AUTH] Keycloak bypassed for local development') // eslint-disable-line no-console
            // Do NOT store a token: getToken() returns null -> the axios interceptor sends an
            // empty Authorization header -> a QAF-pointed backend treats the request as anonymous
            // (see-all) instead of rejecting a foreign/mock bearer token.
            removeAuthDetails()
            localStorage.setItem(ROLE, SAD_ROLE_PREFIX + 'delete')
            renderVueApp()
        },
        logout () {
            removeAuthDetails()
            window.location.href = '/'
        },
        getToken () {
            return localStorage.getItem(TOKEN)
        },
        getUsername () {
            return localStorage.getItem(USERNAME) || 'localdev'
        },
        canView () { return true },
        canEdit () { return true },
        canAdd () { return true },
        canDelete () { return true },
    }
}

export default BYPASS_AUTH ? createBypassAuth() : createKeycloakAuth()
